const permissionPattern = (appLabel: string, codename: string) =>
  `${appLabel}\\.(can_)?${codename}`;

const permissionFromDoc = (permission: string) => {
  const separatorIndex = permission.indexOf(".");
  if (separatorIndex === -1) return permission;

  const appLabel = permission.slice(0, separatorIndex);
  const codename = permission.slice(separatorIndex + 1);

  if (codename.startsWith("can_")) {
    return permissionPattern(appLabel, codename.slice(4));
  }

  return permissionPattern(appLabel, codename);
};

const permissionList = (...permissions: string[]) =>
  permissions.map(permissionFromDoc);

const crudPermissions = (appLabel: string, codename: string) =>
  ({
    view: permissionPattern(appLabel, `view_${codename}`),
    add: permissionPattern(appLabel, `add_${codename}`),
    change: permissionPattern(appLabel, `change_${codename}`),
    delete: permissionPattern(appLabel, `delete_${codename}`)
  }) as const;

const crudPermissionList = (appLabel: string, ...codenames: string[]) =>
  codenames.flatMap((codename) =>
    Object.values(crudPermissions(appLabel, codename))
  );

const REGEX_PATTERN_CHARS = /[\\()[\]{}?+*|^$]/;

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function normalizePermissionPattern(permission: string): string {
  if (permission.includes("\\.")) return permission;

  const separatorIndex = permission.indexOf(".");
  if (separatorIndex === -1) return escapeRegex(permission);

  const appLabel = escapeRegex(permission.slice(0, separatorIndex));
  const codename = permission.slice(separatorIndex + 1);

  if (codename.startsWith("can_")) {
    return `${appLabel}\\.(can_)?${escapeRegex(codename.slice(4))}`;
  }

  if (REGEX_PATTERN_CHARS.test(codename)) {
    return `${appLabel}\\.${codename}`;
  }

  return `${appLabel}\\.(can_)?${escapeRegex(codename)}`;
}

export function matchesPermission(
  userPermission: string,
  permissionPattern: string
): boolean {
  return new RegExp(
    `^${normalizePermissionPattern(permissionPattern)}$`,
    "i"
  ).test(userPermission);
}

export function getGrantedPermissions(value: unknown): string[] | null {
  if (
    !Array.isArray(value) ||
    !value.every((item) => typeof item === "string")
  ) {
    return null;
  }
  return value;
}

export function hasAnyLegacyPermission(
  granted: unknown,
  requested: readonly string[]
): boolean {
  const grantedPermissions = getGrantedPermissions(granted);
  if (!grantedPermissions) return false;
  return grantedPermissions.some((userPermission) =>
    requested.some((permission) =>
      matchesPermission(userPermission, permission)
    )
  );
}

/**
 * Capabilities funcionales publicadas por Backend en
 * `access_control.capabilities` de `GET /api/users/me` (#3071).
 *
 * No son permisos Django: se calculan desde el Permission Profile y son el
 * contrato efectivo de autorización que la UI debe consultar. Por eso NO se
 * comparan con `matchesPermission`, que inyecta el patrón `(can_)?` propio de
 * los codenames de Django.
 */
export const CAPABILITIES = {
  ASSETS: {
    /**
     * Habilita Asset History. Sustituye al permiso interno
     * `dynamic.view_assethistoryevent`, que Backend no publica.
     */
    HISTORY_VIEW: "assets.history.view"
  }
} as const;

/**
 * Compara una capability de forma exacta, ignorando mayúsculas y espacios.
 *
 * Deliberadamente estricta: una capability ausente significa sin acceso. No se
 * aceptan prefijos ni comodines, para que `assets.view` nunca habilite por
 * error algo que requiere `assets.history.view`.
 */
export function matchesCapability(
  userCapability: string,
  capability: string
): boolean {
  return (
    userCapability.trim().toLowerCase() === capability.trim().toLowerCase()
  );
}

/**
 * Resuelve si el contrato de autorización incluye alguna de las capabilities
 * pedidas.
 *
 * Si `capabilities` no viene (entorno sin el contrato desplegado) el resultado
 * es `false`: la UI no habilita accesos sin respaldo del contrato efectivo.
 */
export function hasAnyCapability(
  userCapabilities: string[] | undefined | null,
  capabilities: string[]
): boolean {
  if (!userCapabilities?.length || !capabilities.length) return false;
  return userCapabilities.some((userCapability) =>
    capabilities.some((capability) =>
      matchesCapability(userCapability, capability)
    )
  );
}

/**
 * Qué hacer cuando Backend no sirve `access_control` en absoluto.
 *
 * No hay una respuesta única, y la diferencia es deliberada:
 *
 * - `"deny"`: sin contrato no se habilita nada. Lo exige Asset History (#3157),
 *   donde QA pidió explícitamente no habilitar sin capability efectiva.
 * - `"allow"`: se conserva el comportamiento previo para no ocultar una
 *   superficie que la persona usuaria probablemente sí tiene. Es lo que espera
 *   el filtro de Teams en Work Orders, anterior a la publicación del contrato.
 *
 * Un `access_control` presente pero sin la capability siempre deniega: eso ya
 * es una respuesta del contrato, no su ausencia.
 */
export type CapabilityContractFallback = "allow" | "deny";

export function resolveCapabilityAccess(input: {
  userCapabilities: string[] | undefined | null;
  capabilities: string[];
  whenContractMissing: CapabilityContractFallback;
}): boolean {
  // Sólo la ausencia del campo activa el fallback. Una lista vacía es un
  // contrato servido que no concede nada.
  if (input.userCapabilities === undefined || input.userCapabilities === null) {
    return input.whenContractMissing === "allow";
  }
  return hasAnyCapability(input.userCapabilities, input.capabilities);
}

export const MODULE_PERMISSIONS = {
  ALL_ROLES_GLOBAL: [
    ...permissionList(
      "accounts.view_user",
      "accounts.view_companyuserprofile",
      "accounts.view_approvallevel",
      "accounts.view_governancepolicy",
      "accounts.view_module",
      "accounts.view_activity",
      "accounts.view_notes",
      "accounts.view_userworkspacepreference",
      "companys.view_plan",
      "regions.view_departamento",
      "regions.view_municipio",
      "regions.view_subsubregion",
      "tags.view_tag",
      "shortener.view_shortenedurl",
      "app.view_application",
      "app.view_version"
    )
  ],
  OWNER_ONLY_GLOBAL: [
    ...permissionList(
      "demo_data.add_demobatch",
      "demo_data.view_demobatch",
      "demo_data.delete_demobatch"
    )
  ],
  DASHBOARD_MODULE: [
    ...crudPermissionList("dashboard", "dashboardlayout", "widget")
  ],
  WORK_ORDER_MODULE: [
    ...permissionList(
      "pending_issues.can_add_work_order",
      "pending_issues.can_view_work_order",
      "pending_issues.can_change_work_order",
      "pending_issues.can_delete_work_order",
      "pending_issues.can_approve_work_order",
      "pending_issues.can_undo_approve_work_order",
      "pending_issues.can_admin_cancel_work_order",
      "pending_issues.can_transfer_execution_work_order",
      "pending_issues.can_override_unplanned_activity_type",
      "pending_issues.can_view_all_pending_issue",
      "pending_issues.can_approve_pending_issues",
      "pending_issues.can_archive_work_orders",
      "pending_issues.can_unarchive_work_orders",
      "pending_issues.view_onholdreason"
    ),
    ...crudPermissionList(
      "pending_issues",
      "pendingissues",
      "pendingissueshistory",
      "typependingissue",
      "jobtypeversion",
      "jobtypetools",
      "frequentwork",
      "customworkorderfield",
      "customworkorderfieldoption",
      "maintenanceplan",
      "maintenanceplanasset",
      "maintenanceplanrun",
      "maintenanceplanhistory",
      "templatependingissueversion",
      "templatependingissue",
      "pendingissuetemplatefavorite",
      "bulkoperation",
      "bulkoperationitem"
    ),
    ...crudPermissionList("unplanned", "unplannedactivitytype")
  ],
  ASSETS_MODULE: [
    ...crudPermissionList(
      "dynamic",
      "modelgroup",
      "modelschema",
      "custommodel",
      "fieldmodel",
      "systemcard",
      "titlefieldrelation",
      "subtitlefieldrelation",
      "component",
      "assetcapabilities",
      "assetnote",
      "assethistoryevent"
    )
  ],
  INVENTORY_MODULE: [
    ...permissionList(
      "inventory.view",
      "inventory.import",
      "inventory.manage_catalog",
      "inventory.manage_warehouses",
      "inventory.receive",
      "inventory.transfer",
      "inventory.assign",
      "inventory.assign_vendor",
      "inventory.return",
      "inventory.install",
      "inventory.consume",
      "inventory.adjust",
      "inventory.retire",
      "inventory.view_transactions",
      "inventory.uninstall",
      "inventory.correct_serial",
      "inventory.change_condition"
    ),
    ...crudPermissionList(
      "inventory",
      "inventorycategory",
      "inventorybrand",
      "inventorymodel",
      "inventoryequipment",
      "inventorywarehouse",
      "expectedinventoryitem",
      "inventorycheckresult",
      "importmappingtemplate"
    )
  ],
  FORM_MODULE: [
    ...crudPermissionList(
      "polls",
      "poll",
      "pollversion",
      "categorypoll",
      "question",
      "conditionrule",
      "calculation",
      "pollanswer",
      "questionanswer",
      "questionmediaanswer",
      "pollfavorite",
      "externaldata"
    )
  ],
  FORM_AUDIT_MODULE: [
    ...permissionList(
      "polls.add_poll",
      "polls.view_poll",
      "polls.change_poll",
      "polls.delete_poll",
      "polls.add_pollanswer",
      "polls.view_pollanswer",
      "polls.change_pollanswer",
      "polls.delete_pollanswer",
      "polls.view_questionanswer",
      "polls.view_questionmediaanswer",
      "polls.view_pollversion",
      "polls.view_question"
    )
  ],
  SITE_VISIT_MODULE: [...crudPermissionList("site_visits", "sitevisit")],

  DOCUMENTS_MODULE: [
    ...crudPermissionList(
      "documents",
      "documenttype",
      "document",
      "documentfile",
      "assetreference"
    )
  ],
  LIBRARY_MODULE: [
    ...crudPermissionList(
      "library",
      "folder",
      "file",
      "filehistory",
      "vertical",
      "category",
      "librarytemplate",
      "templatepack",
      "templatedependency",
      "installedtemplate",
      "templateinstallationrun",
      "tenanttemplate",
      "forminstance",
      "formmediaset",
      "formmediashot"
    ),
    ...permissionList("library.can_install_library_template")
  ],
  TEAM_MODULE: [
    ...crudPermissionList("teams", "team", "teammember"),
    ...permissionList("teams.is_owner")
  ],
  WORKSPACE_MODULE: [
    ...crudPermissionList("workspaces", "workspace", "workspacemember"),
    ...permissionList("workspaces.is_owner", "workspaces.is_admin")
  ],
  REPORTS_MODULE: [
    ...crudPermissionList(
      "reports",
      "reports",
      "reporttemplate",
      "typereports",
      "customquery",
      "test",
      "testreports",
      "testdetail"
    )
  ],
  ADMIN_ROLE_MODULE: [
    ...crudPermissionList(
      "accounts",
      "groupinfo",
      "module",
      "companyuserprofile",
      "approvallevel",
      "governancepolicy"
    ),
    ...permissionList(
      "auth.view_permission",
      "contenttypes.view_contenttype",
      "accounts.add_user",
      "accounts.change_user",
      "accounts.delete_user",
      "accounts.add_notes",
      "accounts.change_notes",
      "accounts.delete_notes"
    )
  ],
  MESSAGE_MODULE: [
    ...crudPermissionList("accounts", "inbox"),
    ...crudPermissionList(
      "notifications",
      "notification",
      "notificationevent",
      "notificationrecipient",
      "notificationeventconfig",
      "usernotificationpreference"
    )
  ],
  SETTINGS_MODULE: [
    ...crudPermissionList("auth", "group"),
    ...permissionList(
      "companys.view_owncompany",
      "companys.view_companysitepreferencemodel",
      "companys.view_companyconfiguration",
      "companys.view_typecompany",
      "companys.add_owncompany",
      "companys.change_owncompany",
      "companys.delete_owncompany",
      "companys.change_companysitepreferencemodel",
      "companys.change_companyconfiguration",
      "audit_log.view",
      "audit_log.export"
    )
  ],
  QR_MODULE: [],
  PICTURE_MODULE: [
    ...crudPermissionList(
      "pictures",
      "template",
      "templateversion",
      "templatefavorite",
      "templatecategory",
      "category",
      "typepicture",
      "picture",
      "commentpicture",
      "image"
    )
  ],
  MICROREPORT_MODULE: [
    ...crudPermissionList(
      "microreports",
      "workreason",
      "workperformed",
      "systemunplannedreport",
      "maintenancemicroreport",
      "evidence"
    )
  ],
  PROJECT_MODULE: [
    ...crudPermissionList("projects", "project"),
    ...permissionList("projects.is_owner")
  ],
  TIMESHEET_MODULE: [...crudPermissionList("timesheets", "timesheet")],
  TAG_MODULE: [...crudPermissionList("tags", "tag")],
  BUSINESS_MODULE: [...crudPermissionList("business", "business")],
  SHORTENER_MODULE: [...crudPermissionList("shortener", "shortenedurl")],
  REGION_MODULE: [
    ...crudPermissionList(
      "regions",
      "departamento",
      "municipio",
      "subsubregion"
    ),
    ...permissionList("cities_light.view_country", "cities_light.view_region")
  ],
  APPLICATION_MODULE: [...crudPermissionList("app", "application", "version")],
  OPERATIONAL_TRACKER_MODULE: [
    ...crudPermissionList(
      "tracking",
      "operationaltrackertemplate",
      "operationaltracker",
      "operationaltrackeritem",
      "operationaltrackeritemevent",
      "operationalmilestone",
      "operationalcheckpoint",
      "operationalextrafield",
      "grouprealtime",
      "tracker"
    )
  ],
  MAINTENANCE: crudPermissions("pending_issues", "maintenanceplan"),
  BULK_OPERATIONS: crudPermissions("pending_issues", "bulkoperation"),
  PROJECTS: crudPermissions("projects", "project"),
  INVENTORY: {
    view: permissionPattern("inventory", "view"),
    import: permissionPattern("inventory", "import"),
    viewArchived: permissionPattern("inventory", "view_archived"),
    manageCatalog: permissionPattern("inventory", "manage_catalog"),
    manageWarehouses: permissionPattern("inventory", "manage_warehouses"),
    receive: permissionPattern("inventory", "receive"),
    transfer: permissionPattern("inventory", "transfer"),
    assign: permissionPattern("inventory", "assign"),
    return: permissionPattern("inventory", "return"),
    install: permissionPattern("inventory", "install"),
    consume: permissionPattern("inventory", "consume"),
    adjust: permissionPattern("inventory", "adjust"),
    retire: permissionPattern("inventory", "retire"),
    viewTransactions: permissionPattern("inventory", "view_transactions"),
    viewEquipment: permissionPattern("inventory", "view_inventoryequipment"),
    addEquipment: permissionPattern("inventory", "add_inventoryequipment"),
    changeEquipment: permissionPattern(
      "inventory",
      "change_inventoryequipment"
    ),
    deleteEquipment: permissionPattern("inventory", "delete_inventoryequipment")
  },
  DOCUMENTS: {
    ...crudPermissions("documents", "document"),
    viewType: permissionPattern("documents", "view_documenttype"),
    addType: permissionPattern("documents", "add_documenttype"),
    changeType: permissionPattern("documents", "change_documenttype"),
    deleteType: permissionPattern("documents", "delete_documenttype")
  },
  WORKSPACES: {
    ...crudPermissions("workspaces", "workspace"),
    viewMember: permissionPattern("workspaces", "view_workspacemember"),
    addMember: permissionPattern("workspaces", "add_workspacemember"),
    changeMember: permissionPattern("workspaces", "change_workspacemember"),
    deleteMember: permissionPattern("workspaces", "delete_workspacemember")
  },
  USERS: crudPermissions("accounts", "user"),
  PERMISSION_PROFILES: crudPermissions("auth", "group"),
  COMPANY_SETTINGS: {
    view: permissionPattern("accounts", "view_governancepolicy"),
    change: permissionPattern("accounts", "change_governancepolicy")
  },
  AUDIT_LOG: {
    view: permissionPattern("audit_log", "view"),
    export: permissionPattern("audit_log", "export")
  },
  JOB_TYPES: crudPermissions("pending_issues", "typependingissue"),
  WORK_ORDER_TEMPLATES: crudPermissions(
    "pending_issues",
    "templatependingissue"
  ),
  WORK_ORDER: {
    pending_issue: crudPermissions("pending_issues", "pending_issue"),
    workorders: crudPermissions("pending_issues", "work_order")
  },
  REPORTS: crudPermissions("reports", "reports"),
  TIMESHEETS: crudPermissions("timesheets", "timesheet"),
  MEDIA_TEMPLATES: crudPermissions("pictures", "template"),
  PHOTO_MANAGER: crudPermissions("pictures", "picture"),
  AUDIT_SUBMISSIONS: crudPermissions("polls", "pollanswer"),
  OPERATIONAL_TRACKER: crudPermissions("tracking", "operationaltracker"),
  DYNAMIC: {
    modelGroups: crudPermissions("dynamic", "modelgroup"),
    modelSchemas: crudPermissions("dynamic", "modelschema"),
    customModels: crudPermissions("dynamic", "custommodel"),
    fieldModels: crudPermissions("dynamic", "fieldmodel"),
    systemCards: crudPermissions("dynamic", "systemcard"),
    titleFieldRelations: crudPermissions("dynamic", "titlefieldrelation"),
    subtitleFieldRelations: crudPermissions("dynamic", "subtitlefieldrelation"),
    components: crudPermissions("dynamic", "component"),
    assetCapabilities: crudPermissions("dynamic", "assetcapabilities"),
    assetNotes: crudPermissions("dynamic", "assetnote"),
    /**
     * NO usar para gatear Asset History en la UI. Backend mantiene
     * `view_assethistoryevent` como permiso interno: no lo publica en la lista
     * de permisos del usuario ni en `/api/permission-catalog/`, por lo que
     * `hasPermission` devuelve `false` para todos, incluidos Master Admin a
     * quienes el endpoint sí responde 200 (hallazgo #01 de QA sobre #3157).
     * El acceso al historial lo decide Backend en el endpoint protegido.
     */
    assetHistoryEvents: crudPermissions("dynamic", "assethistoryevent")
  }
} as const;
